import { useState } from 'react'
import { useAdminFetch } from '../hooks/useAdminAPI'
import {
  PageHeader, AdminButton, StatusBadge,
  MetricStrip, Metric, DataTable, ViewState,
  AdminIcon, PipelineFunnel,
} from '../components'
import { getSourceStatus, countSourcesByStatus } from '../utils/health'
import { formatDateTime, formatDuration, computeAcceptRate } from '../utils/format'
import styles from './SourceBreakdown.module.css'

export function SourceBreakdown() {
  const { data, loading, error, refresh } = useAdminFetch('/admin')
  const [expandedSource, setExpandedSource] = useState(null)

  if (loading && !data) return <ViewState variant="loading" />
  if (error && !data) return <ViewState variant="error" message={error} onRetry={refresh} />

  const sourceStats = data?.sourceStats ?? { sources: [], previousSources: {} }
  const sources = sourceStats.sources ?? []

  if (sources.length === 0 && data) {
    return (
      <div className={styles.page}>
        <PageHeader title="Sources" subtitle="Per-supermarket pipeline breakdown" />
        <ViewState variant="empty" message="No source stats found. Stats are recorded after the first scrape." />
      </div>
    )
  }

  const counts = countSourcesByStatus(sources)
  const totalAccepted = sources.reduce((sum, s) => sum + s.accepted, 0)
  const totalExtracted = sources.reduce((sum, s) => sum + s.extracted, 0)

  // Sort: errors first, then zero-offer, then healthy
  const sorted = [...sources].sort((a, b) => {
    const sa = getSourceStatus(a)
    const sb = getSourceStatus(b)
    const order = { error: 0, warning: 1, healthy: 2 }
    return (order[sa] ?? 3) - (order[sb] ?? 3)
  })

  const lastRunSubtitle = sourceStats.run?.startedAt
    ? `Last run: ${formatDateTime(sourceStats.run.startedAt)}`
    : 'Per-supermarket pipeline breakdown'

  return (
    <div className={styles.page}>
      <PageHeader
        title="Sources"
        subtitle={lastRunSubtitle}
        actions={
          <AdminButton
            variant="icon-only"
            icon="refresh"
            label="Refresh data"
            onClick={refresh}
            disabled={loading}
          />
        }
      />

      <MetricStrip>
        <Metric label="Sources" value={sources.length} />
        <Metric label="Total extracted" value={totalExtracted} />
        <Metric label="Total accepted" value={totalAccepted} variant="success" />
        <Metric
          label="With errors"
          value={counts.error}
          variant={counts.error > 0 ? 'error' : 'success'}
        />
        <Metric
          label="Zero offers"
          value={counts.warning}
          variant={counts.warning > 0 ? 'warning' : 'success'}
        />
      </MetricStrip>

      <DataTable>
        <thead>
          <tr>
            <th style={{ width: 24 }}></th>
            <th>Source</th>
            <th>Status</th>
            <th className={styles.numberCol}>Extracted</th>
            <th className={styles.numberCol}>Accepted</th>
            <th className={styles.numberCol}>Rejected</th>
            <th className={styles.numberCol}>Accept rate</th>
            <th className={styles.numberCol}>Duration</th>
            <th className={styles.numberCol}>Trend</th>
          </tr>
        </thead>
        <tbody>
          {sorted.map(source => {
            const status = getSourceStatus(source)
            const acceptRate = computeAcceptRate(source.extracted, source.accepted)
            const previous = sourceStats.previousSources?.[source.source]
            const isExpanded = expandedSource === source.source
            const hasPipelineData = source.fetched != null || source.classified != null

            return (
              <>
                <tr
                  key={source.source}
                  className={`${styles.clickableRow} ${status === 'error' ? styles.rowError : status === 'warning' ? styles.rowWarning : ''} ${isExpanded ? styles.rowExpanded : ''}`}
                  onClick={() => setExpandedSource(isExpanded ? null : source.source)}
                >
                  <td className={styles.expandIcon}>
                    {hasPipelineData && (
                      <AdminIcon name={isExpanded ? 'chevron-down' : 'chevron-right'} size={14} />
                    )}
                  </td>
                  <td className={styles.sourceName}>{source.source}</td>
                  <td><StatusBadge status={status === 'healthy' ? 'ok' : status} /></td>
                  <td className={styles.number}>{source.extracted}</td>
                  <td className={styles.number}>{source.accepted}</td>
                  <td className={styles.number}>{source.rejected}</td>
                  <td className={styles.number}>
                    {acceptRate != null && (
                      <span className={styles.acceptRateCell}>
                        <span className={styles.acceptBar}>
                          <span
                            className={`${styles.acceptFill} ${acceptRate === 0 ? styles.acceptFillZero : acceptRate < 20 ? styles.acceptFillLow : ''}`}
                            style={{ width: `${acceptRate}%` }}
                          />
                        </span>
                        <span>{acceptRate}%</span>
                      </span>
                    )}
                  </td>
                  <td className={`${styles.number} ${styles.mono}`}>
                    {formatDuration(source.durationMs)}
                  </td>
                  <td className={styles.number}>
                    <TrendIndicator current={source.accepted} previous={previous} />
                  </td>
                </tr>
                {isExpanded && hasPipelineData && (
                  <tr key={`${source.source}-detail`} className={styles.detailRow}>
                    <td colSpan={9}>
                      <PipelineFunnel source={source} />
                    </td>
                  </tr>
                )}
              </>
            )
          })}
        </tbody>
      </DataTable>
    </div>
  )
}

function TrendIndicator({ current, previous }) {
  if (previous == null) return <span className={styles.tertiary}>—</span>

  const diff = current - previous
  if (diff === 0) return <span className={styles.tertiary}>→</span>
  if (diff > 0) return <span className={styles.trendUp}>↑{diff}</span>
  return <span className={styles.trendDown}>↓{Math.abs(diff)}</span>
}
